import type * as L from "leaflet";

// Opt-in event log for touch devices without a remote inspector: add ?touchdebug to the URL.
// Shows which touch/gesture events arrive, whether they could be and were cancelled, and
// what the map and the page zoom did.
const MAX_LINES = 16;
const EVENTS = [
  "touchstart",
  "touchmove",
  "touchend",
  "touchcancel",
  "gesturestart",
  "gesturechange",
  "gestureend",
];

export function touchDebug(map: L.Map): () => void {
  if (!new URLSearchParams(location.search).has("touchdebug")) return () => {};

  const panel = document.createElement("pre");
  panel.style.cssText =
    "position:fixed;top:0;left:0;right:0;z-index:100000;margin:0;padding:4px;" +
    "font:10px/1.3 ui-monospace,monospace;background:rgb(0 0 0 / 0.75);color:#fff;" +
    "pointer-events:none;white-space:pre-wrap;max-height:50vh;overflow:hidden";
  document.body.append(panel);

  const lines: string[] = [navigator.userAgent];
  let lastMove = "";
  const log = (line: string) => {
    lines.push(`${(performance.now() / 1000).toFixed(2)} ${line}`);
    lines.splice(1, Math.max(0, lines.length - MAX_LINES));
    panel.textContent = lines.join("\n");
  };

  const onEvent = (e: Event) => {
    const touches = "touches" in e ? (e as TouchEvent).touches.length : "-";
    const scale = (e as Event & { scale?: number }).scale;
    const target = e.target as Element | null;
    // SVG elements (choropleth regions) have an SVGAnimatedString as className
    const targetClass = (target?.getAttribute?.("class") ?? "").split(" ")[0];
    // read defaultPrevented once all listeners have run
    setTimeout(() => {
      const zoomScale = scale === undefined ? "" : ` s=${scale.toFixed(2)}`;
      const line = `${e.type} t=${touches}${zoomScale} c=${+e.cancelable} p=${+e.defaultPrevented} ${target?.tagName?.toLowerCase()}.${targetClass}`;
      // collapse repeated moves
      if (e.type.endsWith("move") || e.type.endsWith("change")) {
        if (line === lastMove) return;
        lastMove = line;
      }
      log(line);
    });
  };
  const onZoom = () => log(`map zoom ${map.getZoom().toFixed(2)}`);
  const onViewport = () =>
    log(`viewport scale ${window.visualViewport?.scale.toFixed(2)}`);
  const onVisibility = () => log(`visibility ${document.visibilityState}`);

  EVENTS.forEach((type) =>
    window.addEventListener(type, onEvent, { passive: true }),
  );
  map.on("zoomend", onZoom);
  window.visualViewport?.addEventListener("resize", onViewport);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    EVENTS.forEach((type) => window.removeEventListener(type, onEvent));
    map.off("zoomend", onZoom);
    window.visualViewport?.removeEventListener("resize", onViewport);
    document.removeEventListener("visibilitychange", onVisibility);
    panel.remove();
  };
}
